import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const Header = () => {
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const base = `/${locale}`;

  const navLinks = [
    { key: "sectors", href: `${base}#sectors` },
    { key: "methodology", href: `${base}#methodology` },
    { key: "transparency", href: `${base}#transparency` },
    { key: "newsletter", href: `${base}#newsletter` },
  ];

  const goToAnchor = (hash) => {
    setOpen(false);
    const [path, anchor] = hash.split("#");
    if (location.pathname !== path) {
      navigate(path);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ block: "start" });
      }, 60);
    } else {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ block: "start" });
    }
  };

  const switchLocale = (target) => {
    if (target === locale) return;
    // Preserve current relative path (after locale)
    const parts = location.pathname.split("/").filter(Boolean);
    let rest = "";
    if (parts[0] === "ar" || parts[0] === "en") {
      rest = parts.slice(1).join("/");
    } else {
      rest = parts.join("/");
    }
    const newPath = "/" + target + (rest ? "/" + rest : "");
    // Mark language switch to preserve scroll position
    window.__voryzix_prev_path = newPath;
    navigate(newPath);
    setOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[#0B192C] border-b border-[#1E293B]"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 lg:h-20 flex items-center justify-between gap-6">
          <Link
            to={base}
            className="flex items-center gap-2 shrink-0"
            data-testid="header-logo"
            onClick={() => setOpen(false)}
          >
            <span className="inline-flex items-center justify-center h-8 w-8 border border-[#C85A32]">
              <span className="font-serif font-bold text-[#C85A32] text-lg leading-none">V</span>
            </span>
            <span className="font-serif font-bold text-white text-lg tracking-tight">Voryzix</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((l) => (
              <button
                key={l.key}
                onClick={() => goToAnchor(l.href)}
                className="text-sm font-medium text-[#CBD5E1] hover:text-white uppercase tracking-wider"
                data-testid={`nav-link-${l.key}`}
              >
                {t(`nav.${l.key}`)}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-1 border border-[#1E293B]">
            <button
              onClick={() => switchLocale("ar")}
              className={
                "px-3 py-1 text-xs font-mono uppercase tracking-widest " +
                (locale === "ar"
                  ? "bg-[#C85A32] text-white"
                  : "text-[#CBD5E1] hover:text-white")
              }
              data-testid="language-switcher-ar"
              aria-label={t("language_switcher.spanish")}
            >
              ES
            </button>
            <button
              onClick={() => switchLocale("en")}
              className={
                "px-3 py-1 text-xs font-mono uppercase tracking-widest " +
                (locale === "en"
                  ? "bg-[#C85A32] text-white"
                  : "text-[#CBD5E1] hover:text-white")
              }
              data-testid="language-switcher-en"
              aria-label={t("language_switcher.english")}
            >
              EN
            </button>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 text-white border border-[#1E293B]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4" data-testid="mobile-nav">
            <nav className="flex flex-col divide-y divide-[#1E293B] border-t border-[#1E293B]">
              {navLinks.map((l) => (
                <button
                  key={l.key}
                  onClick={() => goToAnchor(l.href)}
                  className="text-left py-3 text-sm font-medium text-[#CBD5E1] uppercase tracking-wider"
                  data-testid={`mobile-nav-link-${l.key}`}
                >
                  {t(`nav.${l.key}`)}
                </button>
              ))}
            </nav>
            <div className="flex items-center gap-2 pt-4">
              <span className="text-xs font-mono uppercase tracking-wider text-[#CBD5E1]">
                {t("language_switcher.label")}:
              </span>
              <div className="flex items-center border border-[#1E293B]">
                <button
                  onClick={() => switchLocale("ar")}
                  className={
                    "px-3 py-1 text-xs font-mono uppercase " +
                    (locale === "ar"
                      ? "bg-[#C85A32] text-white"
                      : "text-[#CBD5E1]")
                  }
                  data-testid="mobile-language-switcher-ar"
                >
                  ES
                </button>
                <button
                  onClick={() => switchLocale("en")}
                  className={
                    "px-3 py-1 text-xs font-mono uppercase " +
                    (locale === "en"
                      ? "bg-[#C85A32] text-white"
                      : "text-[#CBD5E1]")
                  }
                  data-testid="mobile-language-switcher-en"
                >
                  EN
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
